import { AuthUIProvider } from '@daveyplate/better-auth-ui'
import { authClient } from './auth-client'
import type { ReactNode } from 'react'
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useLocation } from 'wouter'

// Simple Link component for wouter
const Link = ({ href, to, children, ...props }: any) => {
	const [, setLocation] = useLocation()

	// Support both 'href' and 'to' props (better-auth-ui uses 'href')
	const path = href || to

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		setLocation(path)
	}

	return (
		<a href={path} onClick={handleClick} {...props}>
			{children}
		</a>
	)
}

export function Providers({ children }: { children: ReactNode }) {
	const [, setLocation] = useLocation()
	const hasNavigatedRef = useRef(false)

	// Create a stable navigate function that prevents duplicate navigations
	const handleNavigate = useRef<(href: string) => void>((href: string) => {
		// Prevent duplicate navigations
		if (hasNavigatedRef.current) {
			return
		}

		hasNavigatedRef.current = true

		// Use full page navigation for auth routes to ensure clean unmount
		if (window.location.pathname.includes('/auth/')) {
			window.location.href = href
		} else {
			setLocation(href)
		}

		// Reset after navigation completes
		setTimeout(() => {
			hasNavigatedRef.current = false
		}, 1000)
	}).current

	const handleToast = useCallback(
		({
			variant,
			message,
		}: {
			variant?: 'error' | 'success'
			message: string
		}) => {
			if (variant === 'error') {
				toast.error(message)
			} else if (variant === 'success') {
				toast.success(message)
			} else {
				toast.error(message)
			}
		},
		[]
	)

	return (
		<AuthUIProvider
			authClient={authClient}
			basePath="/auth"
			baseURL={process.env.REACT_APP_CLIENT_URL}
			navigate={handleNavigate}
			Link={Link}
			toast={handleToast}
			signUp={false}
			magicLink
			social={{
				providers: ['google', 'microsoft'],
			}}
		>
			{children}
		</AuthUIProvider>
	)
}
